import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { endOfDayInMilliseconds, startOfDayInMilliseconds } from '@utils/date';
import { defaultStoreDTO } from '@utils/store';

import { TRange } from '@interfaces/common/range';
import { TDefaultStoreDTO } from '@interfaces/common/store';

import { EAssetRentStatuses } from '@enums/asset';
import { defaultMeta } from '@enums/common';
import { TAssetSiteA11yStatus, TBackhaulA11yStatus, TPowerSupplyA11yStatus } from '@enums/map';

interface IInitialState extends TDefaultStoreDTO {
	assetTypeId: string | null;
	assetSubTypeId: string | null;
	contactPersonId: string | null;
	backhaulAccessibilityStatus: TBackhaulA11yStatus | null;
	assetSiteAvailabilityStatus: TAssetSiteA11yStatus | null;
	powerSupplyAvailabilityStatus: TPowerSupplyA11yStatus | null;
	isAvailable: boolean | null;
	createdAt: null | TRange;
	updatedAt: null | TRange;
	lastVerificationDate: null | TRange;
	rentStatus: string | null;
}

const initialState: IInitialState = {
	...defaultMeta,
	sortDirection: false,
	sortFieldName: null,
	searchValue: '',
	assetTypeId: null,
	assetSubTypeId: null,
	contactPersonId: null,
	isAvailable: null,
	backhaulAccessibilityStatus: null,
	assetSiteAvailabilityStatus: null,
	powerSupplyAvailabilityStatus: null,
	createdAt: null,
	updatedAt: null,
	lastVerificationDate: null,
	rentStatus: null,
};

const assets = createSlice({
	name: 'assets',
	initialState,
	reducers: {
		...defaultStoreDTO,
		setAssetType: (state, action: PayloadAction<{ assetType: string | null }>) => {
			state.assetTypeId = action.payload.assetType;
			state.page = initialState.page;
		},
		setAssetSubType: (state, action: PayloadAction<{ assetSubType: string | null }>) => {
			state.assetSubTypeId = action.payload.assetSubType;
			state.page = initialState.page;
		},
		setContactPersonId: (state, action: PayloadAction<{ contactPersonId: string | null }>) => {
			state.contactPersonId = action.payload.contactPersonId;
			state.page = initialState.page;
		},
		setIsAvailable: (state, action: PayloadAction<{ isAvailable: boolean | null }>) => {
			state.isAvailable = action.payload.isAvailable;
			state.page = initialState.page;
		},
		setBackhaulAccessibilityStatus: (
			state,
			action: PayloadAction<{ backhaulAccessibilityStatus: TBackhaulA11yStatus | null }>,
		) => {
			state.backhaulAccessibilityStatus = action.payload.backhaulAccessibilityStatus;
			state.page = initialState.page;
		},
		setAssetSiteAvailabilityStatus: (
			state,
			action: PayloadAction<{ assetSiteAvailabilityStatus: TAssetSiteA11yStatus | null }>,
		) => {
			state.assetSiteAvailabilityStatus = action.payload.assetSiteAvailabilityStatus;
			state.page = initialState.page;
		},
		setPowerSupplyAvailabilityStatus: (
			state,
			action: PayloadAction<{ powerSupplyAvailabilityStatus: TPowerSupplyA11yStatus | null }>,
		) => {
			state.powerSupplyAvailabilityStatus = action.payload.powerSupplyAvailabilityStatus;
			state.page = initialState.page;
		},
		setCreatedAt: (
			state,
			action: PayloadAction<{ createdAt: number | null }>,
		) => {
			if (action.payload.createdAt) {
				state.createdAt = {
					from: startOfDayInMilliseconds(action.payload.createdAt),
					to: endOfDayInMilliseconds(action.payload.createdAt),
				};
			} else {
				state.createdAt = null;
			}

			state.page = initialState.page;
		},
		setUpdatedAt: (
			state,
			action: PayloadAction<{ updatedAt: number | null }>,
		) => {
			if (action.payload.updatedAt) {
				state.updatedAt = {
					from: startOfDayInMilliseconds(action.payload.updatedAt),
					to: endOfDayInMilliseconds(action.payload.updatedAt),
				};
			} else {
				state.updatedAt = null;
			}

			state.page = initialState.page;
		},
		setLastVerificationDate: (
			state,
			action: PayloadAction<{ lastVerificationDate: number | null }>,
		) => {
			if (action.payload.lastVerificationDate) {
				state.lastVerificationDate = {
					from: startOfDayInMilliseconds(action.payload.lastVerificationDate),
					to: endOfDayInMilliseconds(action.payload.lastVerificationDate),
				};
			} else {
				state.lastVerificationDate = null;
			}

			state.page = initialState.page;
		},
		setRentStatus: (
			state,
			action: PayloadAction<{ rentStatus: string | null }>,
		) => {
			const rentStatus = action.payload.rentStatus;

			state.rentStatus = rentStatus === 'All' || rentStatus === null
				? null
				: rentStatus === 'Yes' ? EAssetRentStatuses.active : EAssetRentStatuses.inactive;

			state.page = initialState.page;
		},
		resetAssetsTable: () => initialState,
	},
});

export const {
	setPage,
	setPageSize,
	setSort,
	setSearchValue,
	resetAssetsTable,
	setAssetType,
	setAssetSubType,
	setContactPersonId,
	setIsAvailable,
	setBackhaulAccessibilityStatus,
	setAssetSiteAvailabilityStatus,
	setPowerSupplyAvailabilityStatus,
	setCreatedAt,
	setUpdatedAt,
	setLastVerificationDate,
	setRentStatus,
} = assets.actions;

export default assets.reducer;
